import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import LoginForm from './LoginForm';
import { UncontrolledAlert } from 'reactstrap';
import { CLEAR_SESSION_ERROR, createSession } from 'redux/ducks/Session';
import { capitalize, get } from 'lodash';
import { Link } from 'react-router-dom';
import { isValidReturnUrl } from '../../../helpers/deeplinks';
import { ORGANIZATION_DETAILS_PATH, requireOrganizationData } from '../../../helpers/dashboard';
import Microsoft from 'components/OAuth/Microsoft';
import Google from 'components/OAuth/Google';
import { EXPIRED_SESSION_ERROR_MESSAGE } from 'components/PrivateRoute/PrivateRoute';

class LoginScene extends Component {
  state = { email: '' };

  componentDidMount() {
    const { error } = this.props;
    if (error) this.props.clearSessionError();
  }

  onSubmit = ({ email, password, rememberMe }) => {
    this.props.login({
      email: email,
      password: password,
      rememberMe: rememberMe,
      role: this.props.role,
      history: this.props.history,
    });
  };

  onChooseTitle = () => {
    if (!this.props.role)
      return (
        <div className="v2-title gotham-bold-font">
          <p className="v2-title-p">Log In</p>
        </div>
      );
    return (
      <div className="v2-title gotham-bold-font">
        <p className="v2-title-p">{capitalize(this.props.role)} Portal Log In</p>
      </div>
    );
  };

  setEmail = email => {
    this.setState({ email });
  };

  getMobileAppLink = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/android/i.test(userAgent)) {
      return 'https://play.google.com/store/apps/details?id=com.go4ellis'; // Replace with your Android Play Store link
    }

    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'https://apps.apple.com/app/id1186370661'; // Replace with your iOS App Store link
    }

    return `${process.env.REACT_APP_DEEPLINK_URL}/sign-up`; // fallback for desktop
  };

  renderErrors = () => {
    const { error, session, location } = this.props;
    const { sessionExpired } = session;
    const { email } = this.state;
    const status = get(error, 'response.status');
    const errors = get(error, 'response.data.errors');
    const from = get(location, 'state.from');

    if (sessionExpired || from === 'sessionExpired') {
      return (
        <UncontrolledAlert color="danger" className="alert-margin">
          {EXPIRED_SESSION_ERROR_MESSAGE}
        </UncontrolledAlert>
      );
    }

    if (Array.isArray(errors) && errors.includes('unconfirmed')) {
      return (
        <UncontrolledAlert color="danger" className="alert-margin">
          Your email address has not been verified. Please verify your email to continue.&nbsp;
          <Link to={{ pathname: '/v2/resend-confirmation', state: { email } }}>
            Resend Verification Email
          </Link>
          .
        </UncontrolledAlert>
      );
    }

    // Handle specific backend error messages with priority
    if (error && error.response && error.response.data) {
      const backendError = error.response.data.error;
      const backendMessage = error.response.data.message;
      const backendErrors = error.response.data.errors;

      // Priority 1: Check for specific error messages from the backend
      if (backendMessage && typeof backendMessage === 'string') {
        return (
          <UncontrolledAlert color="danger" className="alert-margin">
            {backendMessage}
          </UncontrolledAlert>
        );
      }

      // Priority 2: Check for specific error keys
      if (backendError) {
        let errorMessage = '';

        switch (backendError) {
          case 'email_already_registered_as_patient':
            errorMessage =
              'This email is already registered as a Patient. Please use the Patient login page.';
            break;
          case 'email_already_registered_as_parent':
            errorMessage = 'This email is already registered as a Parent. Please use the Parent login page.';
            break;
          case 'email_already_registered_as_athletic_trainer':
            errorMessage = (
              <>
                This email address is associated with an Athletic Trainer account. Please use the{' '}
                <a href={this.getMobileAppLink()} target="_blank" rel="noopener noreferrer">
                  Go4 mobile application
                </a>{' '}
                to login.
              </>
            );
            break;
          case 'invalid_credentials':
            errorMessage = 'Invalid email or password. Please check your credentials and try again.';
            break;
          case 'account_locked':
            errorMessage =
              'Your account has been locked due to multiple failed login attempts. Please contact support.';
            break;
          case 'account_disabled':
            errorMessage = 'Your account has been disabled. Please contact support for assistance.';
            break;
          case 'email_not_found':
            errorMessage =
              'No account found with this email address. Please check your email or create a new account.';
            break;
          default:
            // If it's a user-friendly error message, display it as is
            if (backendError.includes(' ') || backendError.length > 20) {
              errorMessage = backendError;
            } else {
              // For technical error codes, provide a generic message
              errorMessage = 'Login failed. Please check your credentials and try again.';
            }
        }

        if (errorMessage) {
          return (
            <UncontrolledAlert color="danger" className="alert-margin">
              {errorMessage}
            </UncontrolledAlert>
          );
        }
      }

      // Priority 3: Check for array of errors
      if (Array.isArray(backendErrors) && backendErrors.length > 0) {
        const firstError = backendErrors[0];
        if (typeof firstError === 'string') {
          return (
            <UncontrolledAlert color="danger" className="alert-margin">
              {firstError}
            </UncontrolledAlert>
          );
        }
      }
    }

    // Fallback to status code based error handling
    if (status) {
      let statusMessage = '';

      switch (status) {
        case 401:
          statusMessage = 'Invalid email or password. Please check your credentials and try again.';
          break;
        case 403:
          statusMessage = 'Access denied. Your account may be restricted or disabled.';
          break;
        case 404:
          statusMessage = 'Account not found. Please check your email address or create a new account.';
          break;
        case 422:
          statusMessage = 'Invalid input. Please check your email and password format.';
          break;
        case 429:
          statusMessage = 'Too many login attempts. Please wait a moment before trying again.';
          break;
        case 500:
        case 502:
        case 503:
          statusMessage = 'Server error. Please try again later or contact support if the problem persists.';
          break;
        default:
          statusMessage = 'An unexpected error occurred. Please try again or contact support.';
      }

      return (
        <UncontrolledAlert color="danger" className="alert-margin">
          {statusMessage}
        </UncontrolledAlert>
      );
    }

    // Final fallback to generic error message
    if (error && error.message) {
      return (
        <UncontrolledAlert color="danger" className="alert-margin">
          {error.message}
        </UncontrolledAlert>
      );
    }

    return null;
  };

  onGoogleLoginSuccess = codeResponse => {
    console.log(codeResponse);
  };

  onGoogleLoginError = () => {
    console.log('Login Failed');
  };

  render() {
    return (
      <React.Fragment>
        <div className="v2LoginScene gotham-regular-font">
          {this.onChooseTitle()}
          {this.renderErrors()}
          {!this.props.role && (
            <Fragment>
              <div className="oauth-container mt-5 mb-4">
                {process.env.REACT_APP_MICROSOFT_OAUTH_ENABLED === 'true' ? (
                  <div className="row">
                    <div className="col-lg-6 gotham-medium-font mb-2 mb-lg-0">
                      <Google />
                    </div>
                    <div className="col-lg-6 gotham-medium-font mt-2 mt-lg-0">
                      <Microsoft />
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    <div className="col-lg-12 gotham-medium-font mb-2 mb-lg-0">
                      <Google />
                    </div>
                  </div>
                )}
              </div>
              <div className="or-divider px-5">
                <div className="divider-line"></div>
                <span>OR</span>
                <div className="divider-line"></div>
              </div>
            </Fragment>
          )}
          <LoginForm
            {...this.props}
            onSubmit={this.onSubmit}
            role={this.props.role}
            from="login"
            setConfirmationEmail={this.setEmail}
          />
        </div>
        <a className="atSignUpHyperlink" href={`${process.env.REACT_APP_DEEPLINK_URL}/sign-up`}>
          Are you an athletic trainer? Click here.
        </a>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const error = state.session.error;
  const session = state.session;
  const role = get(session, 'currentUser.role');

  if (!error && session && state.session.authenticated) {
    const params = new URLSearchParams(ownProps.location.search);
    const returnUrl = params.get('returnUrl');

    if (returnUrl) {
      try {
        const decodedUrl = decodeURIComponent(returnUrl);
        if (isValidReturnUrl(decodedUrl)) {
          ownProps.history.replace(decodedUrl);
          return { error, session };
        }
      } catch (error) {
        console.error('Error processing return URL:', error);
      }
    }

    // Default role-based redirects
    const defaultRedirects = {
      Parent: '/parent/dashboard',
      Patient: '/patient/dashboard',
    };

    let redirectPath = defaultRedirects[role] || '/dashboard';

    if (
      role &&
      role === 'EventOperator' &&
      requireOrganizationData(session.currentUser, session.authenticated)
    ) {
      redirectPath = ORGANIZATION_DETAILS_PATH;
    }

    ownProps.history.push(redirectPath);
  }

  return { error, session };
};

const mapDispatchToProps = dispatch => ({
  login: credentials => dispatch(createSession(credentials)),
  clearSessionError: () => dispatch({ type: CLEAR_SESSION_ERROR }),
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginScene);
